/** Resolves a candidate against the current origin, or null if it lands anywhere else. */
function resolveSameOrigin(candidate: string, origin: string): string | null {
  try {
    const url = new URL(candidate, origin);
    if (url.origin !== origin) return null;
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

/**
 * Builds the same-origin path a hook should hand back to the router or to an Auth0
 * returnTo, with `omitParams` removed from the query. Every caller that feeds
 * `usePathname()` into `router.replace`/`push` or a `returnTo` goes through this — the
 * guard lives inside the builder so no call site can reach the unguarded version.
 *
 * A "starts with / but not //" prefix check is NOT enough: `/\evil.com`, `/\/evil.com`
 * and `/..//evil.com` all pass it and still resolve off-origin, because the WHATWG parser
 * treats a backslash as a slash for special schemes and normalizes dot segments up past
 * the root. Comparing origins is the only reliable test, and it runs twice — the first
 * resolve can normalize `/..//evil.com` into `//evil.com`, which is protocol-relative all
 * over again. Anything that escapes falls back to "/".
 */
export function toSafeStrippedPath(
  pathname: string,
  searchParams: URLSearchParams,
  omitParams: readonly string[],
): string {
  const next = new URLSearchParams(searchParams.toString());
  for (const name of omitParams) next.delete(name);

  const query = next.toString();
  const candidate = query ? `${pathname}?${query}` : pathname;
  const { origin } = window.location;

  const resolved = resolveSameOrigin(candidate, origin);
  if (resolved === null) return "/";

  return resolveSameOrigin(resolved, origin) ?? "/";
}
